import {
  faBrowser,
  faWindowRestore,
} from '@awesome.me/kit-f3dc60f0d0/icons/classic/regular';
import { faPlusCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useLingui } from '@lingui/react/macro';
import {
  ToggleGroup,
  ToggleGroupItem,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@repo/videa-components/src/index';
import classNames from 'classnames';
import { useAtom } from 'jotai';
import { useParams } from 'react-router';

import { FillFindingsIcon } from '@/svgs/imageToolbar/fillFindingsIcon';
import { OutlineFindingsIcon } from '@/svgs/imageToolbar/outlineFindingsIcon';
import { useVisits } from '@/utils/hooks/useVisits';
import { mixpanelEvents, useTrackEvent } from '@/utils/mixpanel';

import { showPastVisitsAtom } from '../atoms';
import { showSegmentationAtom } from '../multiFileViewer/imageToolbar/atoms';

export const ViewModeToggles = () => {
  const [showSegmentations, setShowSegmentations] =
    useAtom(showSegmentationAtom);
  const [showPastVisits, setShowPastVisits] = useAtom(showPastVisitsAtom);
  const { trackEvent } = useTrackEvent();
  const { t } = useLingui();
  const { id: patientId } = useParams<{ id: string }>();
  const { allVisits } = useVisits(patientId);

  // Use showPastVisits to determine review mode (false = review, true = compare)
  const isReviewMode = !showPastVisits;

  // Check if there are multiple visits to show the plus icon
  const hasMultipleVisits = allVisits.length > 1;

  const handleReviewModeToggle = () => {
    trackEvent(mixpanelEvents.reviewModeClick);
    if (!isReviewMode) {
      setShowPastVisits(false);
    }
  };

  const handleCompareModeToggle = () => {
    trackEvent(mixpanelEvents.compareVisits);
    if (isReviewMode) {
      setShowPastVisits(true);
    }
  };

  const toggleGroupItemClassNames = classNames(
    'vai-rounded-sm !vai-p-0 !vai-min-w-0 !vai-min-h-0',
    'max-[880px]:!vai-w-7 max-[880px]:!vai-h-7',
    'min-[880px]:!vai-w-9 min-[880px]:!vai-h-9',
  );
  const activeToggleGroupItemClassNames =
    '!vai-bg-[#0A0A0A] hover:!vai-opacity-70';
  const inactiveToggleGroupItemClassNames =
    '!vai-bg-transparent hover:!vai-bg-[#212734]';

  return (
    <div className="vai-flex vai-flex-col vai-gap-1">
      {/* Review/Compare Mode Toggles */}
      <ToggleGroup
        type="single"
        className="vai-flex vai-flex-col vai-gap-1 vai-bg-[#393E4D] vai-rounded-sm vai-p-1"
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="vai-rounded-sm vai-cursor-pointer">
              <ToggleGroupItem
                value="review"
                onClick={handleReviewModeToggle}
                className={classNames(toggleGroupItemClassNames, {
                  [activeToggleGroupItemClassNames]: isReviewMode,
                  [inactiveToggleGroupItemClassNames]: !isReviewMode,
                })}
              >
                <FontAwesomeIcon
                  icon={faBrowser}
                  className={classNames(
                    'max-[880px]:!vai-w-4 max-[880px]:!vai-h-4 min-[880px]:!vai-w-5 min-[880px]:!vai-h-5',
                    {
                      'vai-text-slate-300': !isReviewMode,
                      'vai-text-sapphire-500': isReviewMode,
                    },
                  )}
                />
              </ToggleGroupItem>
            </div>
          </TooltipTrigger>
          <TooltipContent
            variant="dark"
            side="right"
            sideOffset={8}
            alignOffset={-4}
          >
            <p>{t`Review Mode`}</p>
          </TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <div className="vai-rounded-sm vai-cursor-pointer">
              <ToggleGroupItem
                data-testid="compare-mode-toggle"
                value="compare"
                onClick={handleCompareModeToggle}
                className={classNames(
                  toggleGroupItemClassNames,
                  'vai-relative',
                  {
                    [activeToggleGroupItemClassNames]: !isReviewMode,
                    [inactiveToggleGroupItemClassNames]: isReviewMode,
                  },
                )}
              >
                <FontAwesomeIcon
                  icon={faWindowRestore}
                  className={classNames(
                    'max-[880px]:!vai-w-4 max-[880px]:!vai-h-4 min-[880px]:!vai-w-5 min-[880px]:!vai-h-5',
                    {
                      'vai-text-slate-300': isReviewMode,
                      'vai-text-sapphire-500': !isReviewMode,
                    },
                  )}
                />
                {hasMultipleVisits && (
                  <FontAwesomeIcon
                    icon={faPlusCircle}
                    className={classNames(
                      'vai-absolute !vai-w-3 !vai-h-3 vai-text-cyan-400 vai-top-0.5 vai-right-0.5',
                    )}
                  />
                )}
              </ToggleGroupItem>
            </div>
          </TooltipTrigger>
          <TooltipContent
            variant="dark"
            side="right"
            sideOffset={8}
            alignOffset={-4}
          >
            <p>{t`Compare Mode`}</p>
          </TooltipContent>
        </Tooltip>
      </ToggleGroup>

      {/* Segmentation/Clinical View Toggles */}
      <ToggleGroup
        type="single"
        className="vai-flex vai-flex-col vai-gap-1 vai-bg-[#393E4D] vai-rounded-sm vai-p-1"
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="vai-rounded-sm vai-cursor-pointer">
              <ToggleGroupItem
                data-testid="patient-view-toggle"
                value="fill"
                onClick={() => {
                  trackEvent(mixpanelEvents.patientViewToggle, {
                    patientViewToggle: true,
                    patientId,
                  });
                  setShowSegmentations(true);
                }}
                className={classNames(toggleGroupItemClassNames, {
                  [activeToggleGroupItemClassNames]: showSegmentations,
                  [inactiveToggleGroupItemClassNames]: !showSegmentations,
                })}
              >
                <FillFindingsIcon className="max-[880px]:!vai-w-5 max-[880px]:!vai-h-5 min-[880px]:!vai-w-6 min-[880px]:!vai-h-6" />
              </ToggleGroupItem>
            </div>
          </TooltipTrigger>
          <TooltipContent
            variant="dark"
            side="right"
            sideOffset={8}
            alignOffset={-4}
          >
            <p>{t`Patient View`}</p>
          </TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <div className="vai-rounded-sm vai-cursor-pointer">
              <ToggleGroupItem
                data-testid="clinical-view-toggle"
                value="outline"
                onClick={() => {
                  trackEvent(mixpanelEvents.patientViewToggle, {
                    patientViewToggle: false,
                    patientId,
                  });
                  setShowSegmentations(false);
                }}
                className={classNames(toggleGroupItemClassNames, {
                  [activeToggleGroupItemClassNames]: !showSegmentations,
                  [inactiveToggleGroupItemClassNames]: showSegmentations,
                })}
              >
                <OutlineFindingsIcon className="max-[880px]:!vai-w-5 max-[880px]:!vai-h-5 min-[880px]:!vai-w-6 min-[880px]:!vai-h-6" />
              </ToggleGroupItem>
            </div>
          </TooltipTrigger>
          <TooltipContent
            variant="dark"
            side="right"
            sideOffset={8}
            alignOffset={-4}
          >
            <p>{t`Clinical View`}</p>
          </TooltipContent>
        </Tooltip>
      </ToggleGroup>
    </div>
  );
};
